import 'react-image-crop/dist/ReactCrop.css';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import {
  ChangeEvent, useRef, useState,
} from 'react';
import { useDebounceEffect } from 'src/lib/useDebounceEffect';
import { canvasPreview } from 'src/lib/canvasPreview';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import SaveIcon from '@material-ui/icons/Save';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import Resizer from 'react-image-file-resizer';
import { t } from 'src/lib/language';
import { MODALS } from 'src/models/ModalModel';
import { uploadFileAction } from 'src/redux/actions/uploadFileAction';
import { closeModal } from 'src/redux/actions/modalActions';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { FORM, FORM_STATUS } from 'src/constants/Form';

const useStyles = makeStyles(() => ({
  row: {
    width: '100%',
    padding: '8px 0px',
    display: 'flex',
    justifyContent: 'left',
    gap: '8px',
    alignItems: 'center',
  },
  header: {
    fontSize: '1.2rem',
    fontFamily: 'Qanelas-Bold',
    marginTop: '16px',
  },
  scroller: {
    overflowY: 'auto',
    height: '72vh',
    padding: '8px',
  },
}));

interface ImageSize {
  width: number;
  height: number;
  ratio: number;
}

interface Payload {
  size: ImageSize;
  path: string;
  name: string;
  maxSize?: number;
  onSave?: (file: string, name?: string) => void;
  onUpload?: (path: string) => void;
  conservePath?: boolean;
}

const FormImageCropper = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const formStatus = useSelector(
    (state: Store) => state.formStatus[FORM.upload_image] ?? null,
  );

  const {
    size, path, name, onSave, onUpload, conservePath = false,
  } = useSelector(
    (state: Store) => state.modals[MODALS.uploadImage]?.payload as Payload,
  );

  const InitialCrop: PixelCrop = {
    unit: 'px',
    x: 25,
    y: 25,
    width: size.width,
    height: size.height,
  };
  const lang = useSelector((state: Store) => state.language.language);
  const [crop, setCrop] = useState<Crop>(InitialCrop);
  const [imgSrc, setImgSrc] = useState('');
  const [image, setImage] = useState({} as File);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

  const onSelectFile = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result?.toString();
        if (result) {
          const newImage = new Image();
          newImage.onload = () => {
            if ((newImage.width < size.width) || (newImage.height < size.height)) {
              dispatch(setAlert({
                type: ALERT_TYPE.error,
                message: `Image too small, minimum size is ${size.width}px X ${size.height}px`,
              }));
              return;
            }
            setCrop(InitialCrop);
            setImgSrc(result);
            setImage(selectedFile);
          };
          newImage.src = result;
        }
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const getNormalizedCrop = (pixelCrop: PixelCrop) => {
    if (imgRef.current) {
      const ratio = imgRef.current?.width / imgRef.current?.naturalWidth;
      const { width, height } = pixelCrop;
      return {
        ...pixelCrop,
        width: width * ratio,
        height: height * ratio,
      } as PixelCrop;
    }
    return InitialCrop;
  };

  const onImageLoad = () => {
    setCrop(getNormalizedCrop(InitialCrop));
    setCompletedCrop(getNormalizedCrop(InitialCrop));
  };

  const resizeAndUpload = (result: any) => {
    const commaPosition = result.indexOf(',') + 1;
    const file = result.substring(commaPosition) as string;

    if (onSave) {
      onSave(file, image.name);
      dispatch(closeModal({ modal: MODALS.uploadImage }));
      return;
    }

    // Get extension from original image
    const originalExt = image.name.split('.').pop() || '';
    const ext = originalExt ? `.${originalExt}` : '';

    let filename;
    if (name) {
      filename = `${name}${ext}`; // Use payload name + extension
    } else {
      filename = image.name; // Use original image.name
    }

    const fullPath = conservePath ? path : `${path}/${filename}`;
    const mimeType = result.match(/data:([^;]+);base64,/)?.[1] || 'image/jpeg';

    dispatch(uploadFileAction(
      file,
      fullPath,
      mimeType,
      t(lang, 'messages.successfully_uploaded'),
      t(lang, 'messages.error_upload'),
      onUpload,
    ));
  };

  const onBack = () => {
    dispatch(closeModal({ modal: MODALS.uploadImage }));
  };

  const onSaveFile = () => {
    if (previewCanvasRef.current) {
      previewCanvasRef.current?.toBlob((blob:Nullable<Blob>) => {
        if (blob) {
          const file = new File([blob], name || image?.name);
          Resizer.imageFileResizer(
            file,
            size.width,
            size.height,
            'JPEG', // ← PNG → JPEG
            85, // ← 100 → 85
            0,
            resizeAndUpload,
            'base64',
          );
        }
      });
    }
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        await canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          1,
          0,
        );
      }
    },
    100,
    [completedCrop, crop],
  );
  return (
    <div className="App">
      <div style={{
        display: 'flex', justifyContent: 'flex-end', padding: '0 8px',
      }}>
        <Button size="small" onClick={onBack}>
          {t(lang, 'misc.close')}
        </Button>
      </div>
      <div className={classes.scroller}>
        {!imgSrc ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '48px 32px',
            border: '2px dashed #ccc',
            borderRadius: '8px',
            marginTop: '8px',
          }}>
            <AddIcon style={{ fontSize: '2.5rem', color: '#666' }} />
            <span style={{
              fontSize: '1rem', fontFamily: 'Qanelas-Bold', color: '#666',
            }}>
              Choose an image
            </span>
            <Button variant="contained" component="label" color="primary">
              Browse
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={onSelectFile} />
            </Button>
            <span style={{ fontSize: '0.8rem', color: '#999' }}>
              Minimum size: {size.width} × {size.height} px
            </span>
          </div>
        ) : (
          <>
            <ReactCrop
              crop={crop}
              minHeight={getNormalizedCrop(InitialCrop).height}
              minWidth={getNormalizedCrop(InitialCrop).width}
              style={{ border: '1px dotted #999', borderRadius: '4px' }}
              onChange={(pixelCrop) => setCrop(pixelCrop)}
              onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
              aspect={size.ratio}>
              <img
                ref={imgRef}
                alt="Crop me"
                src={imgSrc}
                onLoad={() => onImageLoad()} />
            </ReactCrop>
            {Boolean(completedCrop) && (
              <>
                <div className={classes.header}>
                  Preview{' '}
                  <span style={{ color: '#888', fontSize: '0.8rem', fontStyle: 'italic' }}>
                    {`— ${size.width} × ${size.height} px`}
                  </span>
                </div>
                <canvas
                  ref={previewCanvasRef}
                  width={size.width / 4}
                  height={size.height / 4}
                  style={{
                    border: '1px solid black',
                    objectFit: 'contain',
                    maxWidth: 800,
                    width: '100%',
                    height: 'auto',
                    marginTop: '8px',
                  }} />
              </>
            )}
            <div className={classes.row}>
              <Button
                variant="contained"
                color="primary"
                onClick={onSaveFile}
                disabled={formStatus === FORM_STATUS.processing}>
                {formStatus === FORM_STATUS.processing ? (
                  <CircularProgress size={20} style={{ color: '#fff' }} />
                ) : (
                  <SaveIcon />
                )}
                Save
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FormImageCropper;
